import { useState, useRef, useEffect, type KeyboardEvent, type ClipboardEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Shield, CheckCircle } from 'lucide-react';
import api from '@/lib/api';

const CODE_LENGTH = 6;

export default function CheckEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const email = sessionStorage.getItem('forgealpha_verify_email') || '';

  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  function handleChange(index: number, value: string) {
    // Only accept digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    setError('');

    // Auto-advance to next input
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits filled
    if (digit && index === CODE_LENGTH - 1 && next.every(d => d !== '')) {
      submitCode(next.join(''));
    }
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;

    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i]!;
    }
    setDigits(next);
    setError('');

    // Focus the next empty or last input
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();

    // Auto-submit if full code pasted
    if (next.every(d => d !== '')) {
      submitCode(next.join(''));
    }
  }

  async function submitCode(code: string) {
    if (!email || verifying) return;
    setVerifying(true);
    setError('');
    try {
      await api.post('/auth/verify-email', { email, code });
      setVerified(true);
      // Redirect to login after brief delay, preserving redirect param
      const loginUrl = redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login';
      setTimeout(() => navigate(loginUrl, { replace: true }), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Verification failed. Please try again.');
      // Clear inputs and refocus first
      setDigits(Array(CODE_LENGTH).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      setVerifying(false);
    }
  }

  async function handleResend() {
    if (!email || resending) return;
    setError('');
    setResending(true);
    try {
      await api.post('/auth/resend-verification', { email });
      setResent(true);
      setDigits(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
      // Reset resent message after 5s
      setTimeout(() => setResent(false), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  }

  if (verified) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}
          >
            <CheckCircle size={28} className="text-green" />
          </div>
          <h1 className="text-xl font-extrabold mb-2">Email Verified!</h1>
          <p className="text-sm text-ink-3 mb-6">Redirecting to sign in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-sm text-center">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
          style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.15), rgba(245,158,11,0.08))', border: '1px solid rgba(249,115,22,0.2)' }}
        >
          <Shield size={28} className="text-orange" />
        </div>

        <h1 className="text-xl font-extrabold mb-2">Enter verification code</h1>
        <p className="text-sm text-ink-3 mb-6 leading-relaxed">
          We sent a 6-digit code to{' '}
          {email ? <strong className="text-ink-2">{email}</strong> : 'your email'}.
        </p>

        {/* Code inputs */}
        <div className="flex justify-center gap-2 mb-6">
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={el => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              onPaste={i === 0 ? handlePaste : undefined}
              disabled={verifying}
              className="w-11 h-13 text-center text-xl font-bold rounded-lg bg-surface text-ink outline-none transition-all duration-200 border border-border focus:border-orange disabled:opacity-50"
              style={{ fontFamily: "'SF Mono', SFMono-Regular, Consolas, monospace" }}
            />
          ))}
        </div>

        {error && <p className="text-xs text-red font-semibold mb-4">{error}</p>}
        {resent && <p className="text-xs text-green font-semibold mb-4">New code sent!</p>}

        {verifying && (
          <p className="text-xs text-ink-3 mb-4">Verifying...</p>
        )}

        {email && (
          <button
            onClick={handleResend}
            disabled={resending || resent}
            className="text-xs text-orange font-semibold hover:underline disabled:opacity-50 mb-4"
          >
            {resent ? 'Code sent!' : resending ? 'Sending...' : "Didn't get a code? Resend"}
          </button>
        )}

        <div className="h-px bg-border my-6" />

        <Link to={redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login'} className="text-xs text-ink-3 hover:text-ink-2">
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
